import React from "react";
import { EntityChipShell, useIsMobile } from "@salestrak/ui";

import { type EntityData } from "#app/lib/entity-visuals";

import { EntityListCard, type OnViewProfile } from "./entity-group";
import { EntityPreview } from "./entity-preview";
import type { Omittable } from "@salestrak/ui";

// Passes NO onClick to the shell so clicks bubble through to whatever wraps it
// (e.g. a selector trigger). MUST sit inside the trigger button, below the
// selector's PopoverTrigger — wrapping the trigger would re-parent it here.
export type EntitySelectionHoverProps = Omittable<{
  entities: EntityData[];
  title?: string;
  /** Suppress the hover preview (e.g. while the selector's picker is open). */
  disabled?: boolean;
  /** Click toggles the list card. Only for standalone chips (profile dialogs,
   *  read-only cells) — never inside selector triggers, where the click must
   *  bubble through to open the picker. Touch devices can't hover, so without
   *  this the list is unreachable there. */
  clickOpensPreview?: boolean;
  onViewProfile?: OnViewProfile;
  children: React.ReactNode;
}>;

export function EntitySelectionHover({
  entities,
  title,
  disabled,
  clickOpensPreview,
  onViewProfile,
  children,
}: EntitySelectionHoverProps) {
  const isMobile = useIsMobile();

  if (entities.length === 0) return <>{children}</>;

  // A lone stage or workflow selection gets its kind's full preview card (the
  // workflow stage ladder) instead of a one-row list — the list card's only
  // extra affordance is per-row view-profile, which those kinds don't have.
  const single = entities.length === 1 ? entities[0] : undefined;
  const previewEntity =
    single && (single.kind === "stage" || single.kind === "workflow")
      ? single
      : undefined;

  return (
    <EntityChipShell
      disableHover={isMobile || disabled}
      clickOpensPreview={clickOpensPreview && !disabled}
      side="bottom"
      align="start"
      // Longer close grace lets the pointer travel from the clickable field
      // into the list before it dismisses.
      closeDelay={300}
      renderPreview={() =>
        previewEntity ? (
          <EntityPreview entity={previewEntity} />
        ) : (
          <EntityListCard
            entities={entities}
            title={title}
            onViewProfile={onViewProfile}
          />
        )
      }
    >
      {children}
    </EntityChipShell>
  );
}
